import {
  BankOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  FacebookFilled,
  LinkedinFilled,
  LinkOutlined,
  TeamOutlined,
  TwitterOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Company, GET_COMPANY } from '@frontend/data-access';
import { formatDate, getLinkLabel } from '@frontend/shared/util';
import { Button, Divider, Modal, Spin } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation'; // Added translation import
import { useMemo, useState } from 'react';

import EditBasicsForm from '../edit-actions/basics';
import EditSocialAccountsForm from '../edit-actions/social-accounts';
import CompanyProfileComplete from './profile-complete';

type actionTypes = 'BASIC_INFO' | 'SOCIAL_ACCOUNTS';

function CompanySideBar() {
  const router = useRouter();
  const id = Number(router.query['companyId']);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [actionType, setActionType] = useState<actionTypes>();
  const { data, loading } = useQuery<{ company: Company }>(GET_COMPANY, {
    variables: { id: id },
  });
  const { t } = useTranslation('common');

  const bankEngagements = useMemo(
    () => [
      {
        value: '1',
        label: t('bank_engagements_no'),
      },
      {
        value: '2',
        label: t('bank_engagements_discussion'),
      },
      {
        value: '3',
        label: t('bank_engagements_poc_phase'),
      },
      {
        value: '4',
        label: t('bank_engagements_production'),
      },
      {
        value: '5',
        label: t('bank_engagements_invested'),
      },
    ],
    [t]
  );

  const itemsBasics = useMemo(
    () => [
      {
        title: t('location'),
        icon: <EnvironmentOutlined />,
        content: data?.company.companyInfo?.location
          ? t('hq', { location: data?.company.companyInfo?.location })
          : null,
      },
      {
        title: t('founded_date'),
        icon: <CalendarOutlined />,
        content: data?.company.foundedDate
          ? t('founded', {
              date: formatDate(data?.company.foundedDate, 'MMM YYYY'),
            })
          : null,
      },
      {
        title: t('team'),
        icon: <TeamOutlined />,
        content: data?.company.companyInfo?.employeeNumber ?? null,
      },
      {
        title: t('engagement'),
        icon: <BankOutlined />,
        content: data?.company.fundingStatus?.bankEngagement?.length
          ? bankEngagements.find(
              (item) =>
                item.value === data?.company.fundingStatus?.bankEngagement
            )?.label
          : null,
      },
    ],
    [
      bankEngagements,
      data?.company.companyInfo?.employeeNumber,
      data?.company.companyInfo?.location,
      data?.company.foundedDate,
      data?.company.fundingStatus?.bankEngagement,
      t,
    ]
  );

  const itemsSocials = [
    {
      title: t('website'),
      icon: <LinkOutlined />,
      content: data?.company.companyInfo?.website ?? null,
    },
    {
      title: t('linkedin'),
      icon: <LinkedinFilled />,
      content: data?.company.companyInfo?.linkedin ?? null,
    },
    {
      title: t('facebook'),
      icon: <FacebookFilled />,
      content: data?.company.companyInfo?.facebook ?? null,
    },
    {
      title: t('twitter'),
      icon: <TwitterOutlined />,
      content: data?.company.companyInfo?.twitter ?? null,
    },
  ];

  const handleModal = (type: actionTypes) => {
    setActionType(type);
    setIsOpenModal(true);
  };

  const handleClose = () => {
    setActionType(undefined);
    setIsOpenModal(false);
  };

  const renderTitle = () => {
    return t('add_type', {
      type: getLinkLabel(actionType?.toLowerCase() as string),
    });
  };

  const renderAction = () => {
    if (data)
      switch (actionType) {
        case 'BASIC_INFO':
          return (
            <EditBasicsForm
              companyId={data?.company.id}
              onCancel={handleClose}
              onFinish={handleClose}
            />
          );
        case 'SOCIAL_ACCOUNTS':
          return (
            <EditSocialAccountsForm
              companyId={data?.company.id}
              onCancel={handleClose}
              onFinish={handleClose}
            />
          );

        default:
          break;
      }
    return <div />;
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <CompanyProfileComplete />
      <div className="rounded-3xl p-5 bg-gray-200 space-y-3">
        {data?.company.isOwner && (
          <div className="flex justify-end">
            <Button
              type="link"
              onClick={() => handleModal('BASIC_INFO')}
              data-test="company-basic-info-edit-btn"
            >
              {t('edit')}
            </Button>
          </div>
        )}
        {itemsBasics.map((item, index) => (
          <div
            key={index}
            className="flex justify-between items-center truncate"
          >
            <div className="flex gap-3 items-center">
              <span className="w-4 text-center">{item.icon}</span>
              {item.content ? (
                <div className="font-medium text-sm text-gray-900">
                  {item.content}
                </div>
              ) : (
                <div className="font-medium text-sm text-gray-700">
                  {t('no_information')}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-3xl p-5 bg-gray-200 space-y-3">
        <div className="flex justify-between items-center">
          <div className="text-gray-900 text-sm font-semibold">
            {t('connect')}
          </div>{' '}
          {data?.company.isOwner && (
            <Button
              type="link"
              onClick={() => handleModal('SOCIAL_ACCOUNTS')}
              data-test="social-edit-btn"
            >
              {t('edit')}
            </Button>
          )}
        </div>
        {itemsSocials
          .filter((i) => i.content)
          .map((item, index) => (
            <div
              key={index}
              className="flex justify-between items-center truncate"
            >
              <div className="flex gap-3 items-center">
                <Button shape="circle">{item.icon}</Button>
                <Link
                  href={item.content as string}
                  className="font-medium text-sm text-primary-600 line-clamp-1"
                >
                  {item.content}
                </Link>
              </div>
            </div>
          ))}
        {itemsSocials.filter((i) => i.title !== 'Website' && i.content)
          .length === 0 && (
          <>
            <Divider />
            <div className="text-center space-y-3 p-4">
              <div className="bg-white py-2 px-2 rounded-lg h-6 w-6 flex justify-center items-center m-auto">
                <LinkOutlined />
              </div>
              <div className="space-y-1">
                <div className="text-gray-900 text-sm">
                  {t('no_social_accounts')}
                </div>
                <p className="text-gray-700 text-sm">{t('insert_links')}</p>
              </div>
            </div>
          </>
        )}
      </div>

      <Modal
        title={renderTitle()}
        open={isOpenModal}
        onCancel={() => handleClose()}
        width={800}
        footer={false}
      >
        {renderAction()}
      </Modal>
    </div>
  );
}

export default CompanySideBar;
